import React from 'react';

interface EarlyAccessProps {
    showModal: boolean;
    setShowModal: (show: boolean) => void;
    spotsTaken: number;
    totalSpots: number;
    loading: boolean;
}

export const EarlyAccess: React.FC<EarlyAccessProps> = ({ showModal, setShowModal, spotsTaken, totalSpots, loading }) => {
    const walletAddress = 'DFEoPTe4AgoHR8VAkwBsevkfdNjjpQJWPf2Gm4K5Y8KJ';
    const [copied, setCopied] = React.useState(false);

    const copyWallet = () => {
        navigator.clipboard.writeText(walletAddress);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <>
            <section id="early-access" className="py-24 bg-black">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-3xl p-12">
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10"></div>
                        
                        <div className="relative z-10">
                            <div className="text-center mb-8">
                                <span className="inline-block px-6 py-3 bg-black/50 border border-purple-500/50 rounded-full text-purple-300 text-lg font-semibold mb-4">
                                    🔐 Early Access
                                </span>
                                <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">Signal Before Build</h2>
                                <p className="text-xl text-gray-300">
                                    NyxBot is in pre-launch. We'll build it if enough of you want it.
                                </p>
                            </div>

                            {/* Progress Indicator */}
                            <div className="mb-8">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-gray-400">Spots Taken</span>
                                    <span className="text-sm font-bold text-white">
                                        {loading ? (
                                            <span className="animate-pulse">Loading...</span>
                                        ) : (
                                            `${spotsTaken} / ${totalSpots}`
                                        )}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
                                    <div 
                                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-1000 ease-out"
                                        style={{ width: `${(spotsTaken / totalSpots) * 100}%` }}
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <span className="text-xs text-gray-400">
                                        {loading ? '...' : `${Math.round((spotsTaken / totalSpots) * 100)}% filled`}
                                    </span>
                                    <span className="text-xs text-orange-400 font-semibold">
                                        {loading ? '' : `${totalSpots - spotsTaken} spots left!`}
                                    </span>
                                </div>
                            </div>

                            <div className="text-center mb-8">
                                <div className="inline-block">
                                    <div className="text-sm text-gray-400 mb-2">Lifetime Access</div>
                                    <div className="text-3xl font-bold text-white">0.1 SOL</div>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-3 gap-4 mb-8">
                                <div className="flex items-center space-x-2 text-white">
                                    <span className="text-xl">✅</span>
                                    <span>Lifetime access</span>
                                </div>
                                <div className="flex items-center space-x-2 text-white">
                                    <span className="text-xl">🧪</span>
                                    <span>First access to test features</span>
                                </div>
                                <div className="flex items-center space-x-2 text-white">
                                    <span className="text-xl">🛡️</span>
                                    <span>Priority in rollout</span>
                                </div>
                            </div>

                            <div className="text-center mb-8">
                                <p className="text-gray-300 mb-2">We launch if 500–1000 people commit.</p>
                                <p className="text-orange-400 font-semibold">
                                    No refunds - this is your sacrifice to NyxBot.
                                </p>
                            </div>

                            <div className="text-center">
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white font-bold text-lg hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-purple-500/25"
                                >
                                    Join Presale Now
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-purple-500/30 rounded-2xl p-8 max-w-md w-full">
                        <div className="flex justify-between items-start mb-6">
                            <h3 className="text-2xl font-bold text-white">Join NyxBot Presale</h3>
                            {!loading && spotsTaken < totalSpots && (
                                <span className="text-sm text-orange-400 font-semibold">
                                    Only {totalSpots - spotsTaken} spots remaining!
                                </span>
                            )}
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <p className="text-gray-300 mb-4">
                            Send <span className="font-bold text-white">0.1 SOL</span> to secure your lifetime access
                        </p>

                        <div className="flex items-center space-x-2 mb-4">
                            <input
                                type="text"
                                value={walletAddress}
                                readOnly
                                className="flex-1 px-4 py-2 bg-black/50 border border-purple-500/30 rounded-lg text-gray-300"
                            />
                            <button
                                onClick={copyWallet}
                                className={`px-4 py-2 rounded-lg text-white font-medium transition-all ${
                                    copied 
                                    ? 'bg-green-500 hover:bg-green-600' 
                                    : 'bg-purple-500 hover:bg-purple-600'
                                }`}
                            >
                                {copied ? (
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Copied!
                                    </span>
                                ) : (
                                    'Copy'
                                )}
                            </button>
                        </div>

                        <div className="space-y-3 mb-6">
                            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                                <p className="text-orange-400 text-sm font-semibold flex items-center">
                                    <span className="mr-2">⚠️</span>
                                    No refunds - this is your commitment to NyxBot
                                </p>
                            </div>
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                                <p className="text-purple-300 text-sm">
                                    <span className="mr-2">🎁</span>
                                    Once 500+ people commit, an exclusive NFT will be airdropped to all participants' sending wallets
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2 text-gray-300">
                            <p>✅ Lifetime access to NyxBot</p>
                            <p>🧪 Early access to new features</p>
                            <p>🛡️ Priority support</p>
                            <p>🎨 Exclusive NFT holder benefits</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};